import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

import { usePageDetails } from "../../hooks/usePageDetails";
import CreateHeading from "../../components/CreateHeading/CreateHeading";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import FormInput from "../../components/FormInput/FormInput";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import FormActionButtons from "../../components/FormActionButtons/FormActionButtons";
import AsyncFormSelect from "../../components/FormSelect/AsyncFormSelect";
import { useLazyGetFamilyListQuery } from "../../app/api/familyApi";
import { useLazyGetBrandListQuery } from "../../app/api/brandApi";
import { useLazyGetModelListQuery } from "../../app/api/modelApi";
import {
  useCreateTemplateMutation,
  useEditTemplateMutation,
  useLazyGetSingleTemplateDetailsQuery,
} from "../../app/api/templateApi";
import { useLazyGetFirmwareListQuery } from "../../app/api/firmwareApi";
import { handleApiResponse } from "../../utils/handleApiResponse";
import { useFilterManager } from "../../hooks/useFilterManager";
import { useAppSelector } from "../../redux/store";

export interface InitialFormValues {
  template_name: string;
  is_active: boolean;
  family_id: string | null;
  brand_id: string | null;
  model_id: string | null;
  firmware_id: string | null;
}

const CreateTemplate: React.FC = () => {
  const { id, pageType } = usePageDetails();
  const { handleFilterChange } = useFilterManager();
  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );

  const [getBrandListQuery] = useLazyGetBrandListQuery();
  const [getFamilyListQuery] = useLazyGetFamilyListQuery();
  const [getModelListQuery] = useLazyGetModelListQuery();
  const [getFirmwareListQuery] = useLazyGetFirmwareListQuery();
  const [createTemplateMutation] = useCreateTemplateMutation();
  const [getSingleTemplateDetailsQuery, getSingleTemplateDetailsResponse] =
    useLazyGetSingleTemplateDetailsQuery();
  const [editTemplateMutation] = useEditTemplateMutation();

  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    template_name: Yup.string()
      .required("Model name is required")
      .min(2, "Model name must be at least 2 character")
      .max(255, "Model name must be at most 255 characters"),
    is_active: Yup.boolean(),
    family_id: Yup.string().required("Family is required"),
    brand_id: Yup.string().required("Brand is required"),
    model_id: Yup.string().required("Model is required"),
    firmware_id:
      pageType === "create"
        ? Yup.string().required("Firmware is required")
        : Yup.string(),
  });

  const initialValues: InitialFormValues = {
    template_name:
      getSingleTemplateDetailsResponse?.data?.data?.template_name ?? "",
    is_active: getSingleTemplateDetailsResponse?.data?.data?.is_active ?? true,
    family_id: persistDropdownSelector["family_selector"]?.value ?? "",
    brand_id: persistDropdownSelector["brand_selector"]?.value ?? "",
    model_id: persistDropdownSelector["model_selector"]?.value ?? "",
    firmware_id:
      pageType !== "create"
        ? (getSingleTemplateDetailsResponse?.data?.data?.firmware_id ?? "")
        : (persistDropdownSelector["firmware_selector"]?.value ?? ""),
  };

  const getSingleTemplateDetails = async () => {
    try {
      await getSingleTemplateDetailsQuery({
        brand_id: persistDropdownSelector["brand_selector"]?.value,
        family_id: persistDropdownSelector["family_selector"]?.value,
        model_id: persistDropdownSelector["model_selector"]?.value,
        template_id: id,
      }).unwrap();
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Get Template Details Error",
      });
    }
  };

  useEffect(() => {
    if (pageType !== "create") {
      getSingleTemplateDetails();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleSubmit = async (
    values: InitialFormValues,
    { setFieldError }: FormikHelpers<InitialFormValues>
  ) => {
    const {
      template_name,
      firmware_id,
      model_id,
      family_id,
      brand_id,
      is_active,
    } = values;

    const body = {
      template_name,
      firmware_id,
      ...(pageType === "edit" ? { is_active } : {}),
    };

    const successMessage =
      pageType === "create"
        ? "Template created successfully."
        : "Template updated successfully.";

    try {
      const response =
        pageType === "create"
          ? await createTemplateMutation({
              body,
              brand_id,
              family_id,
              model_id,
            }).unwrap()
          : await editTemplateMutation({
              body,
              brand_id,
              family_id,
              model_id,
              template_id: id,
            }).unwrap();
      if (response?.status_code === 200 || response?.status_code === 201) {
        handleApiResponse({
          type: "success",
          message: successMessage,
          toastId: "Create Template",
        });
        navigate("/provisioning/template");
      }
    } catch (error: any) {
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Create Template Error",
        setFieldError,
      });
      console.log(error);
    }
  };

  return (
    <Layout>
      <CreateHeading label={"Template"} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          dirty,
        }) => {
          const disable =
            pageType === "create"
              ? !values.template_name ||
                !values.brand_id ||
                !values.family_id ||
                !values.model_id ||
                !values.firmware_id
              : !dirty;

          return (
            <>
              <Form className="w-100 mb-5 pb-5">
                <Col sm={6}>
                  {pageType === "view" && (
                    <Row className="my-2">
                      <FormInput
                        label="Template ID"
                        type="text"
                        name="id"
                        placeholder="Enter template name"
                        labelClassName="m-0 fw-500"
                        controlClassName="bg-white fs-15-point-5 h-48"
                        value={
                          getSingleTemplateDetailsResponse?.data?.data?.id ?? ""
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required={false}
                        isValid={touched.template_name && !errors.template_name}
                        isInvalid={
                          touched.template_name && !!errors.template_name
                        }
                        touched={touched.template_name}
                        error={errors.template_name}
                        disabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  {pageType === "create" ? (
                    <>
                      {" "}
                      <Row className="my-2">
                        <AsyncFormSelect
                          label="Brand"
                          name="brand_id"
                          onChange={(val: string | null, label: string) => {
                            handleFilterChange({
                              updatedField: "brand_id",
                              persistedSelection: {
                                label,
                                value: val,
                              },
                            });
                          }}
                          fetchQuery={getBrandListQuery}
                          fetchQueryParams={{
                            startKey: "",
                            page_size: 5,
                          }}
                          optionLabelName="brand_name"
                          placeholder="Select Brand"
                          required
                          shouldFetch
                          persistedOption={
                            persistDropdownSelector["brand_selector"]
                              ? {
                                  label:
                                    persistDropdownSelector["brand_selector"]
                                      ?.label,
                                  value:
                                    persistDropdownSelector["brand_selector"]
                                      ?.value,
                                }
                              : null
                          }
                          touched={touched.brand_id}
                          error={errors.brand_id}
                          setFieldTouched={setFieldTouched}
                        />
                      </Row>
                      <Row className="my-2">
                        <AsyncFormSelect
                          label="Family"
                          name="family_id"
                          onChange={(val: string | null, label: string) => {
                            handleFilterChange({
                              updatedField: "family_id",
                              persistedSelection: {
                                label,
                                value: val,
                              },
                            });
                          }}
                          fetchQuery={getFamilyListQuery}
                          fetchQueryParams={{
                            startKey: "",
                            page_size: 5,
                            brand_id: values.brand_id,
                          }}
                          optionLabelName="family_name"
                          placeholder="Select Family"
                          isDisabled={!values.brand_id}
                          required
                          key={values.brand_id}
                          shouldFetch={!!values.brand_id?.length}
                          persistedOption={
                            persistDropdownSelector["family_selector"]
                              ? {
                                  label:
                                    persistDropdownSelector["family_selector"]
                                      ?.label,
                                  value:
                                    persistDropdownSelector["family_selector"]
                                      ?.value,
                                }
                              : null
                          }
                          touched={touched.family_id}
                          error={errors.family_id}
                          setFieldTouched={setFieldTouched}
                        />{" "}
                      </Row>
                      <Row className="my-2">
                        <AsyncFormSelect
                          label="Model"
                          name="model_id"
                          onChange={(val: string | null, label: string) => {
                            handleFilterChange({
                              updatedField: "model_id",
                              persistedSelection: {
                                label,
                                value: val,
                              },
                            });
                          }}
                          fetchQuery={getModelListQuery}
                          fetchQueryParams={{
                            startKey: "",
                            page_size: 5,
                            brand_id: values.brand_id,
                            family_id: values.family_id,
                          }}
                          optionLabelName="model_name"
                          placeholder="Select Model"
                          isDisabled={!values.family_id}
                          required
                          key={`${values.brand_id}-${values.family_id}`}
                          shouldFetch={
                            !!values.brand_id?.length &&
                            !!values.family_id?.length
                          }
                          persistedOption={
                            persistDropdownSelector["model_selector"]
                              ? {
                                  label:
                                    persistDropdownSelector["model_selector"]
                                      ?.label,
                                  value:
                                    persistDropdownSelector["model_selector"]
                                      ?.value,
                                }
                              : null
                          }
                          touched={touched.model_id}
                          error={errors.model_id}
                          setFieldTouched={setFieldTouched}
                        />{" "}
                      </Row>
                      <Row className="my-2">
                        <AsyncFormSelect
                          label="Firmware"
                          name="firmware_id"
                          onChange={(val: string | null, label: string) => {
                            handleFilterChange({
                              updatedField: "firmware_id",
                              persistedSelection: {
                                label,
                                value: val,
                              },
                            });
                          }}
                          fetchQuery={getFirmwareListQuery}
                          fetchQueryParams={{
                            startKey: "",
                            page_size: 5,
                            brand_id: values.brand_id,
                            family_id: values.family_id,
                          }}
                          optionLabelName="firmware_version"
                          placeholder="Select Firmware"
                          isDisabled={!values.family_id}
                          required
                          key={`${values.family_id}-${values.brand_id}-${values.brand_id}`}
                          shouldFetch={
                            !!values.brand_id?.length &&
                            !!values.family_id?.length
                          }
                          persistedOption={
                            persistDropdownSelector["firmware_selector"]
                              ? {
                                  label:
                                    persistDropdownSelector["firmware_selector"]
                                      ?.label,
                                  value:
                                    persistDropdownSelector["firmware_selector"]
                                      ?.value,
                                }
                              : null
                          }
                          touched={touched.firmware_id}
                          error={errors.firmware_id}
                          setFieldTouched={setFieldTouched}
                        />{" "}
                      </Row>
                    </>
                  ) : (
                    <>
                      {" "}
                      <Row className="my-2">
                        <FormInput
                          label="Brand Name"
                          type="text"
                          name="brand_name"
                          placeholder="Brand name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleTemplateDetailsResponse?.data?.data
                              ?.brand_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                      <Row className="my-2">
                        <FormInput
                          label="Family Name"
                          type="text"
                          name="family_name"
                          placeholder="Family name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleTemplateDetailsResponse?.data?.data
                              ?.family_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                      <Row className="my-2">
                        <FormInput
                          label="Model Name"
                          type="text"
                          name="model_name"
                          placeholder="Model name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleTemplateDetailsResponse?.data?.data
                              ?.model_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                      <Row className="my-2">
                        <FormInput
                          label="Firmware Version"
                          type="text"
                          name="firmware_version"
                          placeholder="Firmware Version"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleTemplateDetailsResponse?.data?.data
                              ?.firmware_version ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                    </>
                  )}

                  <Row className="my-2">
                    <FormInput
                      label="Template Name"
                      type="text"
                      name="template_name"
                      placeholder="Enter template name"
                      labelClassName="m-0 fw-500"
                      controlClassName="bg-white fs-15-point-5 h-48"
                      value={values.template_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required={true}
                      isValid={touched.template_name && !errors.template_name}
                      isInvalid={
                        touched.template_name && !!errors.template_name
                      }
                      touched={touched.template_name}
                      error={errors.template_name}
                      disabled={pageType === "view"}
                    />
                  </Row>
                  {pageType !== "create" && (
                    <Row className="ms-0 mt-4">
                      <ToggleSwitch
                        activeStatusText="Active"
                        inActiveStatusText="Inactive"
                        value={values.is_active}
                        onChange={(val: boolean) => {
                          setFieldValue("is_active", val);
                        }}
                        key={String(values.is_active)}
                        isDisabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  <FormActionButtons
                    disable={disable}
                    isSubmitting={isSubmitting}
                    navigateUrl="/provisioning/template"
                  />
                </Col>
              </Form>
            </>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default CreateTemplate;
